/**
 * Veeha.jsx (brand) — the assistant's own glyph.
 *
 * Not a speech bubble with a face in it, and not the four-point "AI sparkle"
 * every product reached for in 2025. It is the D'leaps leap-mark itself, drawn
 * as an outline at line-icon weight, with three dots inside it. The mark IS
 * the bubble: the D is speaking. The magenta apostrophe stays where it is on
 * the logo, so the glyph is recognisably the same drawing as the wordmark
 * beside it in the app bar rather than a second mascot.
 *
 * Drawn on lucide's grid — 24x24 viewBox, 1.5 stroke, currentColor — because
 * it sits between Menu and Bell in the app bar and anything on a different
 * grid reads as pasted in. The apostrophe is the one exception: it stays
 * magenta at every size, because it is the only part of the logo that is not
 * turquoise and dropping it to currentColor loses the brand.
 *
 *   <VeehaGlyph size={19} />   line art, for a bar button next to lucide icons
 *   <VeehaDisc size={54} />    filled, on the white disc in the tab bar
 *
 * Dot geometry was tuned by rendering at 20 / 24 / 32 / 48 and looking: at
 * r=0.85 on x = 10.0 / 12.6 / 15.2 they sit centred in the blob and still read
 * as three dots at 20px. Wider and the right dot touches the stroke; tighter
 * and they blur into one at small sizes.
 */

const BLOB = "M8.49 4.59 C8.02 4.62 7.55 4.69 7.11 4.82 C6.67 4.95 6.18 5.08 5.87 5.38 C5.56 5.67 5.33 6.15 5.25 6.58 C5.19 7.01 5.35 7.52 5.45 7.97 C5.55 8.42 5.73 8.84 5.86 9.27 C5.98 9.71 6.1 10.16 6.18 10.61 C6.27 11.07 6.31 11.54 6.37 12.0 C6.43 12.47 6.53 12.95 6.53 13.41 C6.53 13.87 6.42 14.3 6.34 14.74 C6.25 15.19 6.12 15.63 6.01 16.08 C5.9 16.53 5.8 16.97 5.68 17.42 C5.57 17.86 5.41 18.29 5.32 18.74 C5.23 19.19 5.09 19.68 5.16 20.12 C5.23 20.55 5.46 21.04 5.77 21.34 C6.08 21.63 6.57 21.78 7.01 21.89 C7.46 22.0 7.97 22.0 8.44 22.0 C8.91 21.99 9.4 21.94 9.86 21.86 C10.32 21.79 10.76 21.68 11.19 21.54 C11.63 21.4 12.04 21.22 12.45 21.02 C12.86 20.81 13.25 20.58 13.63 20.31 C14.01 20.05 14.38 19.75 14.74 19.43 C15.09 19.12 15.46 18.77 15.77 18.41 C16.08 18.05 16.36 17.68 16.6 17.28 C16.83 16.89 17.03 16.48 17.17 16.05 C17.33 15.62 17.43 15.17 17.5 14.71 C17.57 14.25 17.61 13.75 17.6 13.28 C17.59 12.8 17.55 12.32 17.47 11.86 C17.39 11.41 17.26 10.97 17.11 10.54 C16.95 10.12 16.75 9.7 16.53 9.31 C16.31 8.91 16.08 8.51 15.79 8.14 C15.5 7.77 15.17 7.4 14.82 7.07 C14.48 6.75 14.09 6.46 13.71 6.19 C13.33 5.93 12.94 5.68 12.54 5.48 C12.13 5.27 11.72 5.09 11.28 4.95 C10.85 4.82 10.4 4.72 9.94 4.66 C9.47 4.6 8.96 4.57 8.49 4.59 Z";
const APOS = "M18.77 2.05 C18.67 2.0 18.45 2.11 18.3 2.14 C18.14 2.18 17.95 2.19 17.82 2.24 C17.68 2.29 17.53 2.33 17.48 2.45 C17.42 2.57 17.49 2.79 17.5 2.96 C17.51 3.13 17.53 3.3 17.53 3.47 C17.53 3.63 17.52 3.8 17.5 3.97 C17.48 4.13 17.46 4.3 17.44 4.46 C17.42 4.63 17.4 4.79 17.37 4.95 C17.34 5.12 17.3 5.27 17.27 5.43 C17.24 5.59 17.13 5.82 17.17 5.91 C17.2 6.0 17.38 6.01 17.5 5.97 C17.61 5.93 17.77 5.77 17.88 5.65 C17.99 5.53 18.08 5.38 18.16 5.24 C18.24 5.1 18.32 4.96 18.38 4.82 C18.45 4.67 18.5 4.52 18.55 4.36 C18.59 4.21 18.64 4.05 18.68 3.9 C18.72 3.74 18.75 3.58 18.77 3.42 C18.8 3.26 18.82 3.09 18.84 2.93 C18.86 2.76 18.91 2.58 18.91 2.44 C18.89 2.29 18.88 2.09 18.77 2.05 Z";
const DOTS = [10.0, 12.6, 15.2];

/** Line art. Inherits colour from the button it sits in. */
export function VeehaGlyph({ size = 20, title = "Ask Veeha", strokeWidth = 1.5 }) {
  return (
    <svg
      className="veeha-glyph"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label={title}
      focusable="false"
    >
      <path
        d={BLOB}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path d={APOS} fill="var(--magenta, #E05886)" />
      {DOTS.map((x) => (
        <circle key={x} cx={x} cy="13.4" r="0.85" fill="currentColor" />
      ))}
    </svg>
  );
}

/**
 * Filled, on the white disc inside the mauve-to-magenta ring — the tab bar's
 * centre button. The blob is turquoise and the dots are knocked out of it in
 * white, so at 26px the glyph still reads as three dots rather than as a
 * turquoise smudge.
 */
export function VeehaDisc({ size = 54, title = "Ask Veeha, D'leaps AI" }) {
  const pad = Math.max(3, Math.round(size * 0.055));
  const inner = Math.round((size - pad * 2) * 0.62);
  return (
    <span
      className="veeha-disc"
      style={{
        width: size,
        height: size,
        padding: pad,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
        background: "linear-gradient(135deg, var(--mauve, #cf91bf), var(--mag-btn, #CE3F70))",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,.5)",
      }}
    >
      <span
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          background: "#fff",
          display: "grid",
          placeItems: "center",
        }}
      >
        <svg width={inner} height={inner} viewBox="0 0 24 24" role="img" aria-label={title} focusable="false">
          <path d={BLOB} fill="var(--turq, #62E8EF)" />
          <path d={APOS} fill="var(--magenta, #E05886)" />
          {DOTS.map((x) => (
            <circle key={x} cx={x} cy="13.4" r="1.05" fill="#fff" />
          ))}
        </svg>
      </span>
    </span>
  );
}
